import React, { useState, useRef } from "react";

const Step4FilesMedia = ({
  formData,
  updateFormData,
  nextStep,
  prevStep,
  filePreviews,
  updateFilePreview,
}) => {
  const [errors, setErrors] = useState({});

  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const businessPlanInputRef = useRef(null);

  const handleFileChange = (field, file, inputRef) => {
    if (!file) return;

    // Validate file type
    const isImage = field === "logo" || field === "banner";
    const isPdf = field === "business_plan";

    if (isImage) {
      const validImageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!validImageTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [field]: "Please upload a valid image file (JPEG, PNG, GIF, or WebP)",
        }));
        if (inputRef?.current) inputRef.current.value = "";
        return;
      }

      // Validate image size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [field]: "Image size must be less than 5MB",
        }));
        if (inputRef?.current) inputRef.current.value = "";
        return;
      }
    }

    if (isPdf) {
      if (file.type !== "application/pdf") {
        setErrors((prev) => ({
          ...prev,
          [field]: "Please upload a PDF file",
        }));
        if (inputRef?.current) inputRef.current.value = "";
        return;
      }

      // Validate PDF size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [field]: "PDF size must be less than 10MB",
        }));
        if (inputRef?.current) inputRef.current.value = "";
        return;
      }
    }

    // Clear any previous errors
    setErrors((prev) => ({ ...prev, [field]: null }));

    // Update form data
    updateFormData({ [field]: file });

    // Generate preview
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        updateFilePreview(field, e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      updateFilePreview(field, file.name);
    }
  };

  const handleRemoveFile = (field, inputRef) => {
    updateFormData({ [field]: null });
    updateFilePreview(field, null);
    if (inputRef?.current) inputRef.current.value = "";
  };

  const validateAndNext = () => {
    // Files are optional, so we just proceed
    nextStep();
  };

  return (
    <div className="step-form">
      <div className="form-section">
        <h3 className="section-title">Files & Media</h3>
        <p style={{ color: "#666", marginBottom: "20px" }}>
          Upload campaign media files. All files are optional but recommended.
        </p>

        {/* Logo Upload */}
        <div className="form-group file-upload">
          <label>Company Logo</label>
          <div
            className={`upload-area ${formData.logo ? "has-file" : ""}`}
            onClick={() => logoInputRef.current?.click()}
          >
            {filePreviews.logo ? (
              <div className="preview">
                <img src={filePreviews.logo} alt="Logo preview" />
                <div className="file-info">{formData.logo?.name}</div>
              </div>
            ) : (
              <>
                <div className="upload-icon">📷</div>
                <div className="upload-text">
                  Click to upload or drag and drop
                  <br />
                  <span className="browse-link">Browse files</span>
                </div>
              </>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={(e) => handleFileChange("logo", e.target.files[0], logoInputRef)}
            />
          </div>
          {errors.logo && <div className="error-message">{errors.logo}</div>}
          <div className="help-text">Recommended: Square image, minimum 200x200px. Max 5MB.</div>
          {formData.logo && (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: "10px", padding: "8px 15px" }}
              onClick={() => handleRemoveFile("logo", logoInputRef)}
            >
              Remove Logo
            </button>
          )}
        </div>

        {/* Banner Upload */}
        <div className="form-group file-upload" style={{ marginTop: "30px" }}>
          <label>Campaign Banner</label>
          <div
            className={`upload-area ${formData.banner ? "has-file" : ""}`}
            onClick={() => bannerInputRef.current?.click()}
          >
            {filePreviews.banner ? (
              <div className="preview">
                <img
                  src={filePreviews.banner}
                  alt="Banner preview"
                  style={{ maxWidth: "400px", maxHeight: "200px" }}
                />
                <div className="file-info">{formData.banner?.name}</div>
              </div>
            ) : (
              <>
                <div className="upload-icon">🖼️</div>
                <div className="upload-text">
                  Click to upload or drag and drop
                  <br />
                  <span className="browse-link">Browse files</span>
                </div>
              </>
            )}
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={(e) => handleFileChange("banner", e.target.files[0], bannerInputRef)}
            />
          </div>
          {errors.banner && <div className="error-message">{errors.banner}</div>}
          <div className="help-text">Recommended: 1200x400px or similar aspect ratio. Max 5MB.</div>
          {formData.banner && (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: "10px", padding: "8px 15px" }}
              onClick={() => handleRemoveFile("banner", bannerInputRef)}
            >
              Remove Banner
            </button>
          )}
        </div>

        {/* Business Plan Upload */}
        <div className="form-group file-upload" style={{ marginTop: "30px" }}>
          <label>Business Plan (PDF)</label>
          <div
            className={`upload-area ${formData.business_plan ? "has-file" : ""}`}
            onClick={() => businessPlanInputRef.current?.click()}
          >
            {filePreviews.business_plan ? (
              <div className="preview">
                <div className="pdf-preview">
                  <span className="pdf-icon">📄</span>
                  <span className="pdf-name">{filePreviews.business_plan}</span>
                </div>
              </div>
            ) : (
              <>
                <div className="upload-icon">📄</div>
                <div className="upload-text">
                  Click to upload or drag and drop
                  <br />
                  <span className="browse-link">Browse files</span>
                </div>
              </>
            )}
            <input
              ref={businessPlanInputRef}
              type="file"
              accept="application/pdf"
              onChange={(e) =>
                handleFileChange("business_plan", e.target.files[0], businessPlanInputRef)
              }
            />
          </div>
          {errors.business_plan && <div className="error-message">{errors.business_plan}</div>}
          <div className="help-text">Upload the company's business plan document. Max 10MB.</div>
          {formData.business_plan && (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: "10px", padding: "8px 15px" }}
              onClick={() => handleRemoveFile("business_plan", businessPlanInputRef)}
            >
              Remove Business Plan
            </button>
          )}
        </div>
      </div>

      <div className="step-buttons">
        <button className="btn btn-secondary" onClick={prevStep}>
          Back
        </button>
        <button className="btn btn-primary" onClick={validateAndNext}>
          Next: Review & Submit
        </button>
      </div>
    </div>
  );
};

export default Step4FilesMedia;
